import { format } from 'date-fns';
import dayjs from 'dayjs';
import _debounce from 'lodash/debounce';
import { FC, useMemo } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TechCardsTable } from '@/entities';
import {
	TECH_CARD_ASSEMBLY_NEW,
	TECH_CARD_NEW,
	TECH_CARD_PREFORM_NEW,
	TECH_CARD_RUBBER_NEW,
	UserRoles,
} from '@/shared/core';
import { useAppSelector } from '@/shared/state';
import {
	GetAllTechMapsApiArg,
	GetAssemblyTechMapsApiArg,
	GetPreformTechMapsApiArg,
	GetPreformTechMapsApiResponse,
	GetRubberTechMapsApiArg,
	GetTechMapsApiArg,
	useGetAllTechMapsQuery,
	useGetAssemblyTechMapsQuery,
	useGetPreformTechMapsQuery,
	useGetRubberTechMapsQuery,
	useGetTechMapListExcelMutation,
	useGetTechMapsQuery,
	useLazyGetAllTechMapsExcelQuery,
	useLazyGetAssemblyTechMapsExcelQuery,
	useLazyGetPreformTechMapsExcelQuery,
	useLazyGetRubberTechMapsExcelQuery,
	useLazyGetTechMapWastedOperationsExcelQuery,
	useLazyGetAssemblyTechMapWastedOperationsExcelQuery,
	useLazyGetPreformTechMapWastedOperationsExcelQuery,
	useLazyGetAllTechMapWastedOperationsExcelQuery,
	useLazyGetRubberTechMapWastedOperationsExcelQuery,
} from '@/shared/state/api/swagger';

import { DEFAULT_DATE_FNS_YEAR_FORMAT, MainContentLoader, Search, showErrorToast } from '@/shared';
import {
	Button,
	chakra,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Spinner,
	TextHeader,
	useToast,
	Flex,
	Tabs,
	Tab,
	TabList,
} from '@chakra-ui-kraud/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { MadIcon } from 'madsoft-icons';

import { FilterTechMapModal, Layout } from '@/widgets';
import styles from './tech-cards-page.module.scss';
import { isEmpty } from 'lodash';
import {
	TechCardsFiltersArg,
	TechCardsFiltersNullableArg,
	TechMapData,
	TechMapType,
} from '@/entities/tables/tech-card';

const DEFAULT_QUERY_PARAMS: TechCardsFiltersArg = {
	pageSize: 32,
	currentPage: 1,
	orderBy: 'number',
	orderDir: 'desc',
};

function toTechMapData(preformResponse: GetPreformTechMapsApiResponse | undefined): TechMapData | undefined {
	if (!preformResponse) return undefined;

	return {
		...preformResponse,
		payload: preformResponse.payload.map((preform) => ({
			id: preform.id,
			number: preform.number || 1,
			current_product_operation: preform.current_preform_operation,
			status: preform.status,
			created_at: preform.created_at,
			storage_at: preform.storage_at,
			current_details_count: preform.current_details_count,
			wasted: preform.wasted,
			remaining_count: preform.remaining_count,
			num_gost: preform.preform_num?.gost,
			num_gost_sortament: preform.preform_num?.gost_na_sortament,
			num: preform.preform_num?.num,
			num_sortament: preform.preform_num?.sortament,
			preform_name: preform.preform?.name,
			return_count: preform.preform_plan?.return_count,
			num_symbol: preform.preform_num?.symbol,

			product: undefined,
		})),
	};
}

export const TechCardsPage: FC = () => {
	const [tabIndex, setTabIndex] = useState(0);
	const types = useMemo(() => ['all', 'product', 'assembly', 'preform', 'rubber'] as TechMapType[], []);
	const currentType = types[tabIndex];

	const [queryParams, setQueryParams] = useState<TechCardsFiltersArg>(DEFAULT_QUERY_PARAMS);

	const handleTabsChange = (index: number) => {
		setTabIndex(index);
		setQueryParams(DEFAULT_QUERY_PARAMS);
	};

	const toast = useToast();
	const userRole = useAppSelector((state) => state.auth.userProfile?.role);

	const [getAllCardsExcelByFilters, { isLoading: isGetAllExcelLoading }] = useLazyGetAllTechMapsExcelQuery();
	const [getPreformCardsExcelByFilters, { isLoading: isGetPreformExcelLoading }] =
		useLazyGetPreformTechMapsExcelQuery();
	const [getAssemblyCardsExcelByFilters, { isLoading: isGetAssemblyExcelLoading }] =
		useLazyGetAssemblyTechMapsExcelQuery();
	const [getProductCardsExcelByFilters, { isLoading: isGetProductExcelLoading }] = useGetTechMapListExcelMutation();
	const [getRubberCardsExcelByFilters, { isLoading: isGetRubberExcelLoading }] = useLazyGetRubberTechMapsExcelQuery();

	const [getAllWastedExcelByFilters, { isLoading: isGetAllWastedExcelLoading }] =
		useLazyGetAllTechMapWastedOperationsExcelQuery();
	const [getPreformWastedExcelByFilters, { isLoading: isGetPreformWastedExcelLoading }] =
		useLazyGetPreformTechMapWastedOperationsExcelQuery();
	const [getAssemblyWastedExcelByFilters, { isLoading: isGetAssemblyWastedExcelLoading }] =
		useLazyGetAssemblyTechMapWastedOperationsExcelQuery();
	const [getProductWastedExcelByFilters, { isLoading: isGetProductWastedExcelLoading }] =
		useLazyGetTechMapWastedOperationsExcelQuery();
	const [getRubberWastedExcelByFilters, { isLoading: isGetRubberWastedExcelLoading }] =
		useLazyGetRubberTechMapWastedOperationsExcelQuery();

	const [searchTerm, setSearchTerm] = useState<string>('');

	const [isFiltersModalOpen, setFiltersModalOpen] = useState(false);

	const {
		data: allData,
		isLoading: isAllLoading,
		isFetching: isAllFetching,
		isError: isGetAllError,
	} = useGetAllTechMapsQuery(queryParams as GetAllTechMapsApiArg, {
		refetchOnMountOrArgChange: true,
		skip: currentType !== 'all',
	});

	const {
		data: productData,
		isLoading: isProductLoading,
		isFetching: isProductFetching,
		isError: isGetProductError,
	} = useGetTechMapsQuery(queryParams as GetTechMapsApiArg, {
		refetchOnMountOrArgChange: true,
		skip: currentType !== 'product',
	});
	const {
		assemblyData,
		isLoading: isAssemblyLoading,
		isFetching: isAssemblyFetching,
		isError: isAssemblyMapGetError,
	} = useGetAssemblyTechMapsQuery(queryParams as GetAssemblyTechMapsApiArg, {
		refetchOnMountOrArgChange: true,
		selectFromResult: (result) => ({
			...result,
			assemblyData: {
				...result.data,
				payload: result.data?.payload?.map((item) => ({
					...item,
					product: { ...item.assembly },
					current_product_operation: { ...item.current_assembly_operation },
				})),
			},
		}),
		skip: currentType !== 'assembly',
	});
	const {
		data: preformData,
		isLoading: isPreformLoading,
		isFetching: isPreformFetching,
		isError: isPreformMapGetError,
	} = useGetPreformTechMapsQuery(queryParams as GetPreformTechMapsApiArg, {
		refetchOnMountOrArgChange: true,
		skip: currentType !== 'preform',
	});
	const {
		data: rubberData,
		isLoading: isRubberLoading,
		isFetching: isRubberFetching,
		isError: isRubberMapGetError,
	} = useGetRubberTechMapsQuery(queryParams as GetRubberTechMapsApiArg, {
		refetchOnMountOrArgChange: true,
		skip: currentType !== 'rubber',
	});

	const queries = {
		all: {
			isLoading: isAllLoading,
			isFetching: isAllFetching,
			isGetError: isGetAllError,
			data: allData as TechMapData,
			isGetExcelLoading: isGetAllExcelLoading,
			getCardsExcelByFilters: getAllCardsExcelByFilters,
		},
		assembly: {
			isLoading: isAssemblyLoading,
			isFetching: isAssemblyFetching,
			isGetError: isAssemblyMapGetError,
			data: assemblyData,
			isGetExcelLoading: isGetAssemblyExcelLoading,
			getCardsExcelByFilters: getAssemblyCardsExcelByFilters,
		},
		product: {
			isLoading: isProductLoading,
			isFetching: isProductFetching,
			isGetError: isGetProductError,
			data: productData,
			isGetExcelLoading: isGetProductExcelLoading,
			getCardsExcelByFilters: getProductCardsExcelByFilters,
		},
		preform: {
			isLoading: isPreformLoading,
			isFetching: isPreformFetching,
			isGetError: isPreformMapGetError,
			data: toTechMapData(preformData),
			isGetExcelLoading: isGetPreformExcelLoading,
			getCardsExcelByFilters: getPreformCardsExcelByFilters,
		},
		rubber: {
			isLoading: isRubberLoading,
			isFetching: isRubberFetching,
			isGetError: isRubberMapGetError,
			data: rubberData,
			isGetExcelLoading: isGetRubberExcelLoading,
			getCardsExcelByFilters: getRubberCardsExcelByFilters,
		},
	};

	const { data, isLoading, isFetching, isGetError, isGetExcelLoading, getCardsExcelByFilters } = queries[currentType];

	useEffect(() => {
		if (isGetError) showErrorToast(toast, { description: 'При получении Техкарт произошла ошибка' });
	}, [isGetError]);

	const navigate = useNavigate();

	const handleCreateClick = (path: string) => {
		navigate(path);
	};

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setQueryParams((p) => ({
			...p,
			stringToSearch: e.target.value.trim() || undefined,
			currentPage: 1,
		}));
	};

	const debouncedSearch = useCallback(_debounce(handleSearch, 700), []);

	const handleFilterFormSubmit = (values: TechCardsFiltersNullableArg) => {
		setQueryParams(
			(prev) =>
				({
					...prev,
					...values,
					group: values?.group || undefined,
					name: values?.name || undefined,
					numStrict: values?.numStrict || undefined,
					numSymbolStrict: values?.numSymbolStrict || undefined,
					symbol: values?.symbol || undefined,
					[`${currentType}Name`]:
						values?.[`${currentType}Name` as keyof TechCardsFiltersNullableArg] || undefined,
					[`${currentType}Symbol`]:
						values?.[`${currentType}Symbol` as keyof TechCardsFiltersNullableArg] || undefined,
					currentOperationsNames: isEmpty(values?.currentOperationsNames)
						? undefined
						: values.currentOperationsNames,
					statuses: isEmpty(values.statuses) ? undefined : values.statuses,
					wasted: values.wasted ?? undefined,
					commented: values.commented ?? undefined,
					createdAtFrom: values.createdAtFrom ? dayjs(values.createdAtFrom).format('YYYY-MM-DD') : undefined,
					createdAtTo: values.createdAtTo ? dayjs(values.createdAtTo).format('YYYY-MM-DD') : undefined,
					storageAtFrom: values.storageAtFrom ? dayjs(values.storageAtFrom).format('YYYY-MM-DD') : undefined,
					storageAtTo: values.storageAtTo ? dayjs(values.storageAtTo).format('YYYY-MM-DD') : undefined,
				} as TechCardsFiltersArg),
		);

		setFiltersModalOpen(false);
	};

	const handleGetListExcelTechCards = useCallback(() => {
		let exportPromise: Promise<Blob>;

		switch (currentType) {
			case 'all':
				exportPromise = getAllCardsExcelByFilters(queryParams as GetAllTechMapsApiArg).unwrap();
				break;
			case 'product':
				exportPromise = getProductCardsExcelByFilters(queryParams as GetTechMapsApiArg).unwrap();
				break;
			case 'assembly':
				exportPromise = getAssemblyCardsExcelByFilters(queryParams as GetAssemblyTechMapsApiArg).unwrap();
				break;
			case 'preform':
				exportPromise = getPreformCardsExcelByFilters(queryParams as GetPreformTechMapsApiArg).unwrap();
				break;
			case 'rubber':
				exportPromise = getRubberCardsExcelByFilters(queryParams as GetRubberTechMapsApiArg).unwrap();
				break;
			default:
				throw new Error(`Unknown type: ${currentType}`);
		}

		exportPromise
			.then((res) => {
				const href = window.URL.createObjectURL(new Blob([res]));
				const link = document.createElement('a');
				link.href = href;
				link.download = `Map_list_${format(new Date(), DEFAULT_DATE_FNS_YEAR_FORMAT)}.xlsx`;
				link.click();
				link.remove();
			})
			.catch(() => {
				showErrorToast(toast, { description: 'При скачивании Excel произошла ошибка' });
			});
	}, [
		getAllCardsExcelByFilters,
		getProductCardsExcelByFilters,
		getAssemblyCardsExcelByFilters,
		getPreformCardsExcelByFilters,
		getRubberCardsExcelByFilters,
		queryParams,
		toast,
		currentType,
	]);

	const handleGetWastedExcelTechCards = useCallback(() => {
		let exportPromise: Promise<Blob>;

		switch (currentType) {
			case 'all':
				exportPromise = getAllWastedExcelByFilters(queryParams as GetAllTechMapsApiArg).unwrap();
				break;
			case 'product':
				exportPromise = getProductWastedExcelByFilters(queryParams as GetTechMapsApiArg).unwrap();
				break;
			case 'assembly':
				exportPromise = getAssemblyWastedExcelByFilters(queryParams as GetAssemblyTechMapsApiArg).unwrap();
				break;
			case 'preform':
				exportPromise = getPreformWastedExcelByFilters(queryParams as GetPreformTechMapsApiArg).unwrap();
				break;
			case 'rubber':
				exportPromise = getRubberWastedExcelByFilters(queryParams as GetRubberTechMapsApiArg).unwrap();
				break;
			default:
				throw new Error(`Unknown type: ${currentType}`);
		}

		exportPromise
			.then((res) => {
				const href = window.URL.createObjectURL(new Blob([res]));
				const link = document.createElement('a');
				link.href = href;
				link.download = `Map_wasted_list_${format(new Date(), DEFAULT_DATE_FNS_YEAR_FORMAT)}.xlsx`;
				link.click();
				link.remove();
			})
			.catch(() => {
				showErrorToast(toast, { description: 'При скачивании Excel произошла ошибка' });
			});
	}, [
		getAllWastedExcelByFilters,
		getProductWastedExcelByFilters,
		getAssemblyWastedExcelByFilters,
		getPreformWastedExcelByFilters,
		getRubberWastedExcelByFilters,
		queryParams,
		toast,
		currentType,
	]);

	const isGetWastedExcelLoading =
		isGetAllWastedExcelLoading ||
		isGetProductWastedExcelLoading ||
		isGetAssemblyWastedExcelLoading ||
		isGetPreformWastedExcelLoading ||
		isGetRubberWastedExcelLoading;

	const hideCreateButton = useMemo(() => {
		if (userRole && !['user', 'storekeeper', 'operator'].includes(userRole)) {
			return true;
		}

		return false;
	}, [userRole]);

	return (
		<>
			<Layout
				title={
					<TextHeader className={styles['tech-cards__title']} variant="h1">
						Маршрутные карты
					</TextHeader>
				}
				headerLeftButton={
					hideCreateButton && (
						<Menu>
							<MenuButton as={Button} variant="ghost" rightIcon={<ChevronDownIcon />}>
								Создать карту...
							</MenuButton>
							<MenuList p={2} zIndex="1000" fontWeight="500" minW="190" borderRadius="base">
								<MenuItem py={2} px={4} onClick={() => handleCreateClick(TECH_CARD_NEW)}>
									На деталь
								</MenuItem>
								<MenuItem py={2} px={4} onClick={() => handleCreateClick(TECH_CARD_ASSEMBLY_NEW)}>
									Сборочную
								</MenuItem>
								<MenuItem py={2} px={4} onClick={() => handleCreateClick(TECH_CARD_PREFORM_NEW)}>
									Заготовительную
								</MenuItem>
								<MenuItem py={2} px={4} onClick={() => handleCreateClick(TECH_CARD_RUBBER_NEW)}>
									РТИ
								</MenuItem>
							</MenuList>
						</Menu>
					)
				}
				search={
					<Search
						handleChange={(e) => {
							debouncedSearch(e);
							setSearchTerm(e.target.value);
						}}
						value={searchTerm}
					/>
				}
				filters={
					<chakra.div display="flex" justifyContent="center" alignItems="center" gap="8px">
						<Button
							size="md"
							variant="ghost"
							colorScheme="tertiary"
							rightIcon={
								<MadIcon module="basic" mode="default" size="default" type="outline" name="filter" />
							}
							minW="125px"
							onClick={() => setFiltersModalOpen(true)}
						>
							Фильтры
						</Button>
						<Menu>
							<MenuButton
								as={Button}
								size="md"
								variant="ghost"
								colorScheme="tertiary"
								rightIcon={
									isGetExcelLoading || isGetWastedExcelLoading ? (
										<Spinner size="xs" />
									) : (
										<ChevronDownIcon />
									)
								}
								leftIcon={
									<MadIcon
										module="basic"
										mode="default"
										size="default"
										type="outline"
										name="arrow-down-tray"
									/>
								}
								minW="125px"
							>
								{isGetExcelLoading || isGetWastedExcelLoading ? 'Обработка данных' : 'Выгрузить данные'}
							</MenuButton>
							<MenuList p={2} zIndex="1000" fontWeight="500" minW="220" borderRadius="base">
								<MenuItem py={2} px={4} onClick={handleGetListExcelTechCards}>
									МК
								</MenuItem>
								<MenuItem py={2} px={4} onClick={handleGetWastedExcelTechCards}>
									Брак
								</MenuItem>
							</MenuList>
						</Menu>
					</chakra.div>
				}
			>
				{isFetching && !isLoading ? <MainContentLoader /> : <></>}

				<TechCardsTable
					type={currentType}
					superHeader={
						<Flex justify="space-between" alignItems="center" mb="1rem">
							<Tabs index={tabIndex} onChange={handleTabsChange}>
								<TabList>
									<Tab>Все</Tab>
									<Tab>На деталь</Tab>
									<Tab>Сборочные</Tab>
									<Tab>Заготовительные</Tab>
									<Tab>РТИ</Tab>
								</TabList>
							</Tabs>
						</Flex>
					}
					data={data}
					setQueryParams={setQueryParams}
					isLoading={isLoading}
					isStatusLoading={isFetching}
					queryParams={queryParams}
				/>
			</Layout>
			{isFiltersModalOpen && (
				<FilterTechMapModal
					type={currentType}
					isOpen={isFiltersModalOpen}
					onClose={() => setFiltersModalOpen(false)}
					onSubmit={handleFilterFormSubmit}
					defaultValue={queryParams}
				/>
			)}
		</>
	);
};
